import React, { useState, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths } from 'date-fns';
import { initialSeasonMonth, nextSeasonMonth, prevSeasonMonth, SEASON_HINT, firstMonthWithSlots, monthOfDateString, canGoToPrevMonth, nextMonthWithSlotsAfter } from '../../utils/season';
import { useAuth } from '../../contexts/AuthContext';
import { savePendingSlot, loadPendingSlot, clearPendingSlot } from '../../utils/pendingSlot';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import CalendarService from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import ImprovedBookingModal from './ImprovedBookingModal';
import BookingSuccessModal from './BookingSuccessModal';
import SlotInquiryModal from './SlotInquiryModal';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import './ImprovedBookingCalendar.css';

interface GroupedSlots {
  [date: string]: CalendarSlot[];
}

// Calculate Easter Sunday using the Anonymous Gregorian algorithm
const getEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Get German holidays for Bayern (Bavaria) for a given year
const getGermanHolidays = (year: number): Set<string> => {
  const holidays = new Set<string>();

  // Fixed holidays
  holidays.add(`${year}-01-01`); // Neujahr
  holidays.add(`${year}-01-06`); // Heilige Drei Könige (Bayern)
  holidays.add(`${year}-05-01`); // Tag der Arbeit
  holidays.add(`${year}-08-15`); // Mariä Himmelfahrt (Bayern)
  holidays.add(`${year}-10-03`); // Tag der Deutschen Einheit
  holidays.add(`${year}-11-01`); // Allerheiligen (Bayern)
  holidays.add(`${year}-12-25`); // 1. Weihnachtsfeiertag
  holidays.add(`${year}-12-26`); // 2. Weihnachtsfeiertag

  // Easter-based holidays
  const easter = getEasterSunday(year);

  // Karfreitag (Good Friday) - Easter - 2 days
  const karfreitag = new Date(easter);
  karfreitag.setDate(easter.getDate() - 2);
  holidays.add(format(karfreitag, 'yyyy-MM-dd'));

  // Ostermontag (Easter Monday) - Easter + 1 day
  const ostermontag = new Date(easter);
  ostermontag.setDate(easter.getDate() + 1);
  holidays.add(format(ostermontag, 'yyyy-MM-dd'));

  // Christi Himmelfahrt (Ascension) - Easter + 39 days
  const himmelfahrt = new Date(easter);
  himmelfahrt.setDate(easter.getDate() + 39);
  holidays.add(format(himmelfahrt, 'yyyy-MM-dd'));

  // Pfingstmontag (Whit Monday) - Easter + 50 days
  const pfingstmontag = new Date(easter);
  pfingstmontag.setDate(easter.getDate() + 50);
  holidays.add(format(pfingstmontag, 'yyyy-MM-dd'));

  // Fronleichnam (Corpus Christi) - Easter + 60 days (Bayern)
  const fronleichnam = new Date(easter);
  fronleichnam.setDate(easter.getDate() + 60);
  holidays.add(format(fronleichnam, 'yyyy-MM-dd'));

  return holidays;
};

interface BookingSuccessData {
  bookingId: number;
  bookingNumber?: string;
  scheduleDate: string;
  scheduleTime: string;
  tourType: string;
  participantCount: number;
  totalAmount: number;
  voucherCode?: string;
  voucherDiscount?: number;
  // T-020: Zahlungsart aus der gespeicherten Buchung (Komplettzahlung gewaehlt bzw. kurzfristig)
  isShortTermBooking?: boolean;
  fullPaymentChosen?: boolean;
}

// T-115 (v1.8.33, Mobil-Befund 27.09.2026): Unter 640 px Breite zeigt der Kalender eine Liste der Tage mit Terminen statt des
// Sieben-Spalten-Rasters (das Raster war auf 375 px breiter als der Bildschirm). Desktop unverändert.
const NARROW_QUERY = '(max-width: 640px)';
const useIsNarrow = (): boolean => {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
  const [narrow, setNarrow] = useState<boolean>(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return narrow;
};

const ImprovedBookingCalendar: React.FC = () => {
  const navigate = useNavigate();
  // T-115 (v1.8.31): Kalender ist ohne Anmeldung sichtbar (Route /booking öffentlich); Buchen und Anfragen nur angemeldet
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  // T-104 (v1.8.25): Kalender startet im laufenden Saisonmonat, außerhalb der Saison im nächsten Mai.
  // T-114/T-115 (v1.8.31): danach Sprung in den Monat des ersten Termins ab heute (startResolved).
  const [currentMonth, setCurrentMonth] = useState(initialSeasonMonth());
  const [startResolved, setStartResolved] = useState(false);
  const [nextTermsFrom, setNextTermsFrom] = useState<Date | null>(null);
  // T-119 (v1.8.33): Termine ab heute (18 Monate) für den Hinweis in Monaten ohne Termine
  const [upcomingDates, setUpcomingDates] = useState<string[]>([]);
  const isNarrow = useIsNarrow();
  const [loadedMonthKey, setLoadedMonthKey] = useState<string | null>(null);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const startDoneRef = useRef(false);
  // Review v1.8.31 Befund 8: gemerkter Termin wird beim Start übernommen und sofort aus dem Speicher gelöscht
  const resumeRef = useRef<{ date: string; time: string } | null>(null);
  // Komponente noch eingehängt? (kein Abbruch über den Effekt-Cleanup: im React-StrictMode läuft der Effekt doppelt,
  // der zweite Lauf endet am startDoneRef, der Cleanup des ersten würde den Startmonat sonst nie setzen)
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [groupedSlots, setGroupedSlots] = useState<GroupedSlots>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<BookingSuccessData | null>(null);

  // T-114/T-115 (v1.8.31): Startmonat bestimmen, sobald die Anmeldung geklärt ist. Angemeldet mit gemerktem Termin
  // (Rückkehr nach Anmeldung): Monat dieses Termins. Sonst: Monat des ersten Termins ab heute (18 Monate Vorschau).
  useEffect(() => {
    if (authLoading || startDoneRef.current) return;
    startDoneRef.current = true;
    const resolveStart = async () => {
      const pending = isAuthenticated ? loadPendingSlot() : null;
      if (pending) {
        resumeRef.current = { date: pending.date, time: pending.time };
        clearPendingSlot();
        setCurrentMonth(monthOfDateString(pending.date));
        setStartResolved(true);
        // T-119 (v1.8.33): Terminliste für den Sprung-Hinweis im Hintergrund laden (Startmonat bleibt der gemerkte Termin)
        CalendarService.getAvailableSlots(new Date(), addMonths(new Date(), 18), undefined, true)
          .then(list => { if (mountedRef.current) setUpcomingDates(list.map(s => s.date)); })
          .catch(() => undefined);
        return;
      }
      try {
        const today = new Date();
        const upcoming = await CalendarService.getAvailableSlots(today, addMonths(today, 18), undefined, true);
        if (mountedRef.current) setUpcomingDates(upcoming.map(s => s.date));
        const first = firstMonthWithSlots(upcoming.map(s => s.date), today);
        if (mountedRef.current && first) {
          setCurrentMonth(first);
          if (first.getTime() > startOfMonth(today).getTime()) {
            setNextTermsFrom(first);
          }
        }
      } catch (err) {
        console.error('Startmonat konnte nicht bestimmt werden, Saisonmonat bleibt:', err);
      } finally {
        if (mountedRef.current) setStartResolved(true);
      }
    };
    resolveStart();
  }, [authLoading, isAuthenticated]);

  // Load slots for current month
  useEffect(() => {
    if (!startResolved) return;
    loadMonthSlots();
  }, [currentMonth, startResolved]);

  // Group slots by date whenever slots change
  useEffect(() => {
    const grouped = slots.reduce((acc: GroupedSlots, slot) => {
      if (!acc[slot.date]) {
        acc[slot.date] = [];
      }
      acc[slot.date].push(slot);
      return acc;
    }, {});

    // Sort slots within each day by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time.localeCompare(b.time));
    });

    setGroupedSlots(grouped);
  }, [slots]);

  // Group slots by time for display (merge same-time variants)
  // WICHTIG: Alle Tour-Typen teilen sich die Kapazität im selben Timeslot!
  const groupSlotsByTime = (daySlots: CalendarSlot[]) => {
    const timeGroups: { [time: string]: CalendarSlot[] } = {};

    daySlots.forEach(slot => {
      if (!timeGroups[slot.time]) {
        timeGroups[slot.time] = [];
      }
      timeGroups[slot.time].push(slot);
    });

    // Convert to array and sort by time
    return Object.entries(timeGroups)
      .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
      .map(([time, slotsAtTime]) => {
        // TIMESLOT-KAPAZITÄT: Alle Tour-Typen teilen sich die Buchungen
        // currentBookings ist identisch für alle Slots im Timeslot (Backend liefert Timeslot-Gesamtwert)
        // Nehme den MAXIMALEN verfügbaren Platz aus allen Tour-Typen
        const maxAvailable = Math.max(...slotsAtTime.map(s => s.availableCapacity));
        // Die höchste MaxKapazität im Timeslot
        const highestMax = Math.max(...slotsAtTime.map(s => s.maxCapacity));

        return {
          time,
          slots: slotsAtTime,
          // KORREKT: Zeige den maximalen verfügbaren Platz (nicht summieren!)
          totalAvailable: maxAvailable,
          totalMax: highestMax,
          // Use the first slot's data for common properties
          isAvailable: slotsAtTime.some(s => s.isAvailable && !s.isBlocked && s.availableCapacity > 0),
          // Befund B7: Timeslot gilt als "geplant", wenn KEIN Slot freigegeben ist
          // (alle Slots eines Timeslots teilen sich den Schedule und damit den Freigabestatus)
          isPlanned: slotsAtTime.every(s => s.isReleasedForOnlineBooking === false)
        };
      });
  };

  const loadMonthSlots = async () => {
    setLoading(true);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      // Befund B7: geplante, noch nicht freigegebene Termine mitladen
      // (isReleasedForOnlineBooking === false), sie sind sichtbar und anfragbar, aber nicht buchbar.
      const data = await CalendarService.getAvailableSlots(start, end, undefined, true);
      setSlots(data);
      setLoadedMonthKey(format(currentMonth, 'yyyy-MM'));
    } catch (err) {
      console.error('Error loading slots:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = (start.getDay() + 6) % 7;
    const emptyDays = Array(firstDayOfWeek).fill(null);
    return [...emptyDays, ...days];
  };

  const getSlotColor = (slot: CalendarSlot): string => {
    if (!slot.isAvailable || slot.isBlocked) return '#999';

    const availabilityPercent = (slot.availableCapacity / slot.maxCapacity) * 100;

    if (availabilityPercent === 0) return '#dc2626'; // Red - Full
    if (availabilityPercent <= 20) return '#f59e0b'; // Orange - Almost full
    if (availabilityPercent <= 50) return '#84cc16'; // Yellow - Half full
    return '#059669'; // Green - Available
  };

  const getTimeGroupColor = (totalAvailable: number, totalMax: number, isPast: boolean): string => {
    // Past slots are always gray
    if (isPast) return '#999';

    if (totalAvailable === 0) return '#dc2626'; // Red - Full

    const availabilityPercent = (totalAvailable / totalMax) * 100;

    if (availabilityPercent <= 20) return '#f59e0b'; // Orange - Almost full
    if (availabilityPercent <= 50) return '#84cc16'; // Yellow - Half full
    return '#059669'; // Green - Available
  };

  const isSlotInPast = (dateStr: string, time: string): boolean => {
    const now = new Date();
    const slotDate = new Date(dateStr);

    // Parse time (format: "HH:mm")
    const [hours, minutes] = time.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);

    return slotDate < now;
  };

  const getTourTypeName = (tourType: string): string => {
    const names: { [key: string]: string } = {
      'STANDARD': 'Standard',
      'ALL_IN': 'All-In',
      'ALL_IN_PREMIUM': 'All-In Premium',
      'EXCLUSIVE': 'Exklusiv',
      'WEDDING': 'Hochzeit'
    };
    return names[tourType] || tourType;
  };

  // Format time: "11:00:00" → "11:00"
  const formatTime = (time: string): string => {
    const parts = time.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
  };

  // Format capacity: Show available capacity per variant separated by "/"
  // e.g., 2 variants with 140 and 50 free → "140/50"
  // e.g., 3 variants with 140, 50, 110 free → "140/50/110"
  const formatCapacity = (slots: CalendarSlot[]): string => {
    if (slots.length === 0) return '0';

    // Sort by tourType for consistent order
    const sortedSlots = [...slots].sort((a, b) => a.tourType.localeCompare(b.tourType));

    // Get available capacity for each variant
    const capacities = sortedSlots.map(s => s.availableCapacity);

    return capacities.join('/');
  };

  const handleSlotClick = (slot: CalendarSlot) => {
    // Check if slot is in the past
    const isPast = isSlotInPast(slot.date, slot.time);
    if (isPast) {
      return; // Don't allow booking past slots
    }

    // T-115 (v1.8.31): ohne Anmeldung Termin merken und zur Anmeldung/Registrierung führen, nur für Termine,
    // die angemeldet einen Dialog öffnen würden (geplant = Anfrage, freigegeben mit freien Plätzen = Buchung; Review Befund 3)
    const oeffnetDialog = slot.isReleasedForOnlineBooking === false
      || (slot.isAvailable && !slot.isBlocked && slot.availableCapacity > 0);
    if (!isAuthenticated) {
      if (!oeffnetDialog) return;
      savePendingSlot(slot.date, slot.time);
      setSelectedSlot(slot);
      setShowLoginHint(true);
      return;
    }

    // Befund B7: geplante, noch nicht freigegebene Termine öffnen das Anfrage-Formular
    // statt der Buchung (buchbar sind sie serverseitig ohnehin nicht).
    if (slot.isReleasedForOnlineBooking === false) {
      setSelectedSlot(slot);
      setShowInquiryModal(true);
      return;
    }

    if (slot.isAvailable && !slot.isBlocked && slot.availableCapacity > 0) {
      setSelectedSlot(slot);
      setShowBookingModal(true);
    }
  };

  // T-115 (v1.8.31): nach der Anmeldung den gemerkten Termin wieder öffnen, sobald sein Monat geladen ist
  useEffect(() => {
    if (!isAuthenticated || !startResolved || loading || !loadedMonthKey) return;
    const pending = resumeRef.current;
    if (!pending) return;
    if (pending.date.substring(0, 7) !== loadedMonthKey) return;
    resumeRef.current = null;
    const sameTime = (a: string, b: string) => a.substring(0, 5) === b.substring(0, 5);
    const candidates = slots.filter(s => s.date === pending.date && sameTime(s.time, pending.time));
    if (candidates.length === 0) return; // Termin inzwischen nicht mehr angeboten
    setSelectedDate(pending.date);
    const firstAvailable = candidates.find(s => s.availableCapacity > 0) || candidates[0];
    handleSlotClick(firstAvailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, startResolved, loading, loadedMonthKey, slots]);

  const handleBookingSuccess = async (bookingId: number, voucherCode?: string, voucherDiscount?: number) => {
    setShowBookingModal(false);

    // Fetch booking details from API
    // T-020 (02.09.2026): Erfolgsdialog zeigt die GEBUCHTEN Daten (Tour-Typ des gebuchten Slots,
    // gewaehlte Zahlungsart), nicht den vorausgewaehlten Kalender-Slot. authFetch statt rohem fetch
    // gegen den relativen Pfad /api (lokal ohne Proxy nicht erreichbar, produktiv ohne 401-Refresh).
    try {
      const response = await authFetch(`${API_BASE_URL}/bookings/${bookingId}`);

      if (response.ok) {
        const booking = await response.json();
        const subtotal = booking.pricing?.totalGross || booking.totalPrice || 0;
        const discount = voucherDiscount || 0;
        setSuccessData({
          bookingId: booking.id,
          bookingNumber: booking.bookingNumber,
          scheduleDate: booking.scheduleDate || selectedSlot?.date || '',
          scheduleTime: booking.startTime || selectedSlot?.time || '',
          tourType: booking.tourTypeName || selectedSlot?.tourType || 'Standard',
          participantCount: booking.participantCount || 0,
          totalAmount: Math.max(0, subtotal - discount),
          voucherCode,
          voucherDiscount: discount,
          isShortTermBooking: booking.isShortTermBooking === true,
          fullPaymentChosen: booking.fullPaymentChosen === true
        });
        setShowSuccessModal(true);
      } else {
        // Fallback: Use selected slot data if API fails
        setSuccessData({
          bookingId,
          scheduleDate: selectedSlot?.date || '',
          scheduleTime: selectedSlot?.time || '',
          tourType: selectedSlot?.tourType || 'Standard',
          participantCount: 0,
          totalAmount: 0,
          voucherCode,
          voucherDiscount
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Fallback: Use selected slot data
      setSuccessData({
        bookingId,
        scheduleDate: selectedSlot?.date || '',
        scheduleTime: selectedSlot?.time || '',
        tourType: selectedSlot?.tourType || 'Standard',
        participantCount: 0,
        totalAmount: 0,
        voucherCode,
        voucherDiscount
      });
      setShowSuccessModal(true);
    }

    setSelectedSlot(null);
    loadMonthSlots(); // Reload to show updated capacity
  };

  const handlePayNow = () => {
    if (successData) {
      const params = new URLSearchParams();
      if (successData.voucherCode) {
        params.set('voucherCode', successData.voucherCode);
      }
      if (successData.voucherDiscount) {
        params.set('voucherDiscount', successData.voucherDiscount.toString());
      }
      const queryString = params.toString();
      navigate(`/checkout/${successData.bookingId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleViewBooking = () => {
    if (successData) {
      const params = new URLSearchParams();
      if (successData.voucherCode) {
        params.set('voucherCode', successData.voucherCode);
      }
      if (successData.voucherDiscount) {
        params.set('voucherDiscount', successData.voucherDiscount.toString());
      }
      const queryString = params.toString();
      navigate(`/booking-confirmation/${successData.bookingId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
  };

  // T-104 (v1.8.25): Saison Mai bis Oktober, Oktober -> Mai des Folgejahres, Mai -> Oktober des Vorjahres
  // T-119 (v1.8.33): nicht in vergangene Monate zurückblättern
  const prevAllowed = canGoToPrevMonth(currentMonth);
  const handlePrevMonth = () => { if (canGoToPrevMonth(currentMonth)) setCurrentMonth(prevSeasonMonth(currentMonth)); };
  const handleNextMonth = () => setCurrentMonth(nextSeasonMonth(currentMonth));

  const closeLoginHint = () => {
    clearPendingSlot();
    setShowLoginHint(false);
    setSelectedSlot(null);
  };

  // Review v1.8.31 Befund 9: Anmeldehinweis mit Escape schließen
  useEffect(() => {
    if (!showLoginHint) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearPendingSlot();
        setShowLoginHint(false);
        setSelectedSlot(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLoginHint]);

  // T-115 (v1.8.33): Terminkacheln eines Tages, gemeinsam für Raster (Desktop) und Liste (Mobil)
  const renderTimeGroups = (dateStr: string, timeGroups: ReturnType<typeof groupSlotsByTime>) => (
      <div className="timeslots">
        {timeGroups.map((timeGroup, tgIdx) => {
          const isPast = isSlotInPast(dateStr, timeGroup.time);
          const isPlanned = !isPast && timeGroup.isPlanned;
          const baseColor = isPlanned ? '#2563eb' : getTimeGroupColor(timeGroup.totalAvailable, timeGroup.totalMax, isPast);

          // Create lighter and darker versions for stronger gradient
          let lighterColor, darkerColor;
          if (isPast) {
            // Special gray gradient for past slots - blasser
            lighterColor = '#d0d0d0'; // Very light gray
            darkerColor = '#9a9a9a'; // Medium gray
          } else {
            // More intense colors for active slots
            lighterColor = baseColor + '80'; // 50% opacity for lighter start (was 99/60%)
            darkerColor = baseColor; // Full opacity for darker end
          }

          const title = isPast
            ? `${formatTime(timeGroup.time)} Uhr - Vergangen`
            : isPlanned
              ? `${formatTime(timeGroup.time)} Uhr - Termin in Planung, jetzt anfragen`
              : `${formatTime(timeGroup.time)} Uhr - ${timeGroup.totalAvailable} Plätze frei`;

          return (
            <div
              key={`${dateStr}-${timeGroup.time}-${tgIdx}`}
              className={`timeslot ${timeGroup.totalAvailable === 0 ? 'full' : ''} ${isPast ? 'past' : ''} ${isPlanned ? 'planned' : ''}`}
              style={{
                background: `linear-gradient(135deg, ${lighterColor} 0%, ${darkerColor} 100%)`,
                borderColor: isPast ? '#808080' : baseColor
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isPast) {
                  // Click on first available slot in group
                  const firstAvailableSlot = timeGroup.slots.find(s => s.availableCapacity > 0) || timeGroup.slots[0];
                  handleSlotClick(firstAvailableSlot);
                }
              }}
              title={title}
            >
              <span className="timeslot-time">{formatTime(timeGroup.time)} Uhr</span>
              <span className="timeslot-capacity">
                {isPast ? 'Vergangen' : isPlanned ? 'auf Anfrage' : formatCapacity(timeGroup.slots)}
              </span>
            </div>
          );
        })}
      </div>
  );

  // T-119 (v1.8.33): Hat der angezeigte Monat Termine ab heute? Sonst Hinweis mit Sprung zum nächsten Monat mit Terminen
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const monthHasUpcoming = slots.some(s => typeof s.date === 'string' && s.date.substring(0, 10) >= todayKey);
  const nextWithSlots = nextMonthWithSlotsAfter(upcomingDates, currentMonth);

  return (
    <div className="improved-booking-calendar">
      {/* T-115 (v1.8.31): Hinweis für Besucher ohne Anmeldung */}
      {!authLoading && !isAuthenticated && (
        <div
          className="public-calendar-hint"
          style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#1e3a8a', fontSize: 14 }}
        >
          Terminübersicht: Hier sehen Sie alle Termine mit Uhrzeit und freien Plätzen. Zum Buchen oder Anfragen klicken Sie auf einen Termin
          und melden sich an oder registrieren sich kostenlos; der gewählte Termin wird danach wieder geöffnet.
        </div>
      )}
      {nextTermsFrom && format(currentMonth, 'yyyy-MM') === format(nextTermsFrom, 'yyyy-MM') && (
        <div
          className="next-terms-hint"
          style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', marginBottom: 12, color: '#14532d', fontSize: 14 }}
        >
          Die nächsten Termine finden Sie ab {format(nextTermsFrom, 'MMMM yyyy', { locale: de })}.
        </div>
      )}
      <div className="calendar-header">
        <button onClick={handlePrevMonth} className="month-nav-btn" disabled={!startResolved || !prevAllowed} aria-label="Vorheriger Monat" title={prevAllowed ? 'Vorheriger Monat' : 'Frühere Monate liegen in der Vergangenheit'}>
          ←
        </button>
        <h2 className="month-title" title={SEASON_HINT}>
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </h2>
        <button onClick={handleNextMonth} className="month-nav-btn" disabled={!startResolved} aria-label="Nächster Monat" title="Nächster Monat">
          →
        </button>
      </div>

      {/* T-119 (v1.8.33): Monat ohne (künftige) Termine: Hinweis und Sprung zum nächsten Monat mit Terminen */}
      {startResolved && !loading && loadedMonthKey === format(currentMonth, 'yyyy-MM') && !monthHasUpcoming && (
        <div
          className="empty-month-hint"
          style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#7c2d12', fontSize: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}
        >
          <span>Im {format(currentMonth, 'MMMM yyyy', { locale: de })} gibt es keine buchbaren Termine.</span>
          {nextWithSlots && (
            <button
              type="button"
              className="empty-month-jump"
              onClick={() => setCurrentMonth(nextWithSlots)}
              style={{ background: '#910F0F', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 14 }}
            >
              Nächste Termine im {format(nextWithSlots, 'MMMM yyyy', { locale: de })} anzeigen
            </button>
          )}
        </div>
      )}

      {(loading || !startResolved) && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {!isNarrow && (
        <>
        <div className="weekdays-header">
          {['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-grid">
          {getDaysInMonth().map((date, idx) => {
            if (!date) {
              return <div key={`empty-${idx}`} className="calendar-day empty" />;
            }

            const dateStr = format(date, 'yyyy-MM-dd');
            const daySlots = groupedSlots[dateStr] || [];
            const timeGroups = groupSlotsByTime(daySlots);
            const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const isSelected = selectedDate === dateStr;
            const holidays = getGermanHolidays(date.getFullYear());
            const isHoliday = holidays.has(dateStr);

            return (
              <div
                key={idx}
                className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedDate(dateStr)}
              >
                <div className={`day-number ${isWeekend ? 'weekend' : ''} ${isHoliday ? 'holiday' : ''}`}>
                  {format(date, 'd')}
                </div>

                {timeGroups.length > 0 && renderTimeGroups(dateStr, timeGroups)}

                {timeGroups.length === 0 && (
                  <div className="no-slots">Keine Termine</div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {isNarrow && (
        <div className="calendar-list">
          {getDaysInMonth()
            .filter((date): date is Date => !!date)
            .map(date => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const timeGroups = groupSlotsByTime(groupedSlots[dateStr] || []);
              // Liste nur mit Tagen ab heute (vergangene Tage würden die Liste auf dem Smartphone nur verlängern)
              if (timeGroups.length === 0 || dateStr < todayKey) return null;
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isHoliday = getGermanHolidays(date.getFullYear()).has(dateStr);
              return (
                <div key={dateStr} className={`calendar-list-day ${selectedDate === dateStr ? 'selected' : ''}`} onClick={() => setSelectedDate(dateStr)}>
                  <div className={`calendar-list-date ${isWeekend ? 'weekend' : ''} ${isHoliday ? 'holiday' : ''}`}>
                    {format(date, 'EEEE, d. MMMM', { locale: de })}
                  </div>
                  {renderTimeGroups(dateStr, timeGroups)}
                </div>
              );
            })}
        </div>
      )}

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#059669' }}></span>
          <span>Verfügbar</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#84cc16' }}></span>
          <span>Wenige Plätze</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f59e0b' }}></span>
          <span>Fast ausgebucht</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#dc2626' }}></span>
          <span>Ausgebucht</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#2563eb' }}></span>
          <span>In Planung, auf Anfrage</span>
        </div>
      </div>

      {/* T-115 (v1.8.31): Anmeldung erforderlich (Besucher ohne Anmeldung hat einen Termin gewählt) */}
      {showLoginHint && selectedSlot && (
        <div
          className="login-hint-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={closeLoginHint}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-hint-title"
            style={{ background: '#ffffff', color: '#1f2937', borderRadius: 12, padding: 24, maxWidth: 420, width: '100%', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 id="login-hint-title" style={{ marginTop: 0, color: '#1f2937' }}>Bitte anmelden</h3>
            <p style={{ margin: '0 0 8px 0' }}>
              Termin: {format(new Date(`${selectedSlot.date}T00:00:00`), 'EEEE, dd.MM.yyyy', { locale: de })}, {formatTime(selectedSlot.time)} Uhr
              {selectedSlot.isReleasedForOnlineBooking === false ? ' (auf Anfrage)' : ''}
            </p>
            <p style={{ margin: '0 0 16px 0' }}>
              Zum {selectedSlot.isReleasedForOnlineBooking === false ? 'Anfragen' : 'Buchen'} melden Sie sich bitte mit Ihrem Kundenkonto an
              oder registrieren sich kostenlos. Der gewählte Termin wird danach in diesem Browser wieder geöffnet.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeLoginHint} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#ffffff', color: '#374151', cursor: 'pointer' }}>
                Abbrechen
              </button>
              <button type="button" onClick={() => navigate('/register')} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #2563eb', background: '#ffffff', color: '#2563eb', cursor: 'pointer' }}>
                Registrieren
              </button>
              <button type="button" autoFocus onClick={() => navigate('/login')} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#ffffff', cursor: 'pointer' }}>
                Anmelden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <ImprovedBookingModal
          slot={selectedSlot}
          availableSlotsForDay={(groupedSlots[selectedSlot.date] || [selectedSlot])
            .filter(s => s.isReleasedForOnlineBooking !== false) /* Befund B7: geplante Termine nicht im Buchungsdialog anbieten */}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedSlot(null);
          }}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Terminanfrage-Modal für geplante, noch nicht freigegebene Termine (Befund B7) */}
      {showInquiryModal && selectedSlot && (
        <SlotInquiryModal
          slot={selectedSlot}
          onClose={() => {
            setShowInquiryModal(false);
            setSelectedSlot(null);
          }}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <BookingSuccessModal
          bookingId={successData.bookingId}
          bookingNumber={successData.bookingNumber}
          scheduleDate={successData.scheduleDate}
          scheduleTime={successData.scheduleTime}
          tourType={successData.tourType}
          participantCount={successData.participantCount}
          totalAmount={successData.totalAmount}
          voucherCode={successData.voucherCode}
          voucherDiscount={successData.voucherDiscount}
          isShortTermBooking={successData.isShortTermBooking}
          fullPaymentChosen={successData.fullPaymentChosen}
          onClose={handleCloseSuccess}
          onPayNow={handlePayNow}
          onViewBooking={handleViewBooking}
        />
      )}
    </div>
  );
};

export default ImprovedBookingCalendar;
